import { useState, useRef, useCallback, useEffect } from 'react'
import Color from '@assets/images/Color.png'
import ColorPickerHover from '@assets/images/ColorPickerHover.png'
import { styled } from 'baseui'
import ColorPickerPopover, { COMPACT_COLOR_PICKER_HEIGHT } from './ColorPickerPopover'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'

export type ColorPickerPopoverPlacement =
  | 'right'
  | 'left'
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

const WrapPickerTrigger = styled('div', (props: { $isSecondaryStyle?: boolean; $fluid?: boolean }) => ({
  width: props.$fluid ? '100%' : props.$isSecondaryStyle ? '32px' : '40px',
  height: props.$fluid ? 'auto' : props.$isSecondaryStyle ? '32px' : '40px',
  ...(props.$fluid ? { aspectRatio: '1 / 1', padding: 0, ':focus-visible': { outline: `2px solid ${lightTheme.colors.primary}`, outlineOffset: '2px' } } : {}),
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  borderWidth: '0',
  transition: 'all 0.25s ease-in-out',
  ':hover': {
    cursor: 'pointer',
  },
}))

export interface ColorPickerButtonProps {
  colorSelected: string
  onChange: (color: string, isFinal: boolean) => void
  isSecondaryStyle?: boolean
  popoverPlacement?: ColorPickerPopoverPlacement
  popoverOffset?: { x?: number; y?: number }
  /** Phones: a smaller popover placed beside the trigger, kept on screen. */
  compact?: boolean
  fluid?: boolean
}

const COMPACT_MAX_WIDTH = 240
const COMPACT_GAP = 8
const VIEWPORT_MARGIN = 8

function ColorPickerButton({
  colorSelected,
  onChange,
  isSecondaryStyle,
  popoverPlacement = 'right',
  popoverOffset,
  compact = false,
  fluid = false,
}: ColorPickerButtonProps) {
  const { t } = useTranslation()
  const [isOpenPopover, setIsOpenPopover] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverPosition, setPopoverPosition] = useState<null | { top: number; left: number; width?: number; maxHeight?: number }>(null)

  const calculatePopoverPosition = useCallback(
    (rect: DOMRect): { top: number; left: number } => {
      const offsetX = popoverOffset?.x || 0
      const offsetY = popoverOffset?.y || 0
      const popoverWidth = 292
      const popoverHeight = 322
      let top = 0
      let left = 0

      switch (popoverPlacement) {
        case 'right':
          top = rect.top + offsetY
          left = rect.right + offsetX
          break
        case 'left':
          top = rect.top + offsetY
          left = rect.left - popoverWidth - offsetX
          break
        case 'top':
          top = rect.top - popoverHeight - offsetY
          left = rect.left + (rect.width - popoverWidth) / 2 + offsetX
          break
        case 'bottom':
          top = rect.bottom + offsetY
          left = rect.left + (rect.width - popoverWidth) / 2 + offsetX
          break
        case 'top-left':
          top = rect.top - popoverHeight - offsetY
          left = rect.left + offsetX
          break
        case 'top-right':
          top = rect.top - popoverHeight - offsetY
          left = rect.right - popoverWidth - offsetX
          break
        case 'bottom-left':
          top = rect.bottom + offsetY
          left = rect.left + offsetX
          break
        case 'bottom-right':
          top = rect.bottom + offsetY
          left = rect.right - popoverWidth - offsetX
          break
      }

      return { top, left }
    },
    [popoverPlacement, popoverOffset]
  )

  useEffect(() => {
    if (!isOpenPopover || !buttonRef.current) return

    const updatePosition = () => {
      if (!buttonRef.current) return
      const rect = buttonRef.current.getBoundingClientRect()
      if (compact) {
        const viewport = window.visualViewport
        const viewportTop = viewport?.offsetTop ?? 0
        const viewportLeft = viewport?.offsetLeft ?? 0
        const viewportRight = viewportLeft + (viewport?.width ?? window.innerWidth)
        const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight)
        const spaceRight = viewportRight - rect.right - COMPACT_GAP - VIEWPORT_MARGIN
        const spaceLeft = rect.left - viewportLeft - COMPACT_GAP - VIEWPORT_MARGIN
        const opensLeft = spaceRight < COMPACT_MAX_WIDTH && spaceLeft > spaceRight
        const width = Math.max(0, Math.min(COMPACT_MAX_WIDTH, opensLeft ? spaceLeft : spaceRight))
        const maxHeight = Math.max(0, viewportBottom - viewportTop - VIEWPORT_MARGIN * 2)
        const height = Math.min(COMPACT_COLOR_PICKER_HEIGHT, maxHeight)
        const centered = rect.top + rect.height / 2 - height / 2
        const top = Math.max(viewportTop + VIEWPORT_MARGIN, Math.min(centered, viewportBottom - height - VIEWPORT_MARGIN))
        const left = opensLeft ? rect.left - COMPACT_GAP - width : rect.right + COMPACT_GAP
        setPopoverPosition(prev => prev?.top === top && prev.left === left && prev.width === width && prev.maxHeight === maxHeight ? prev : { top, left, width, maxHeight })
        return
      }
      setPopoverPosition(calculatePopoverPosition(rect))
    }
    updatePosition()
    if (!compact) return

    let frame: number | null = null
    const schedulePosition = () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        frame = null
        updatePosition()
      })
    }
    const viewport = window.visualViewport
    window.addEventListener('resize', schedulePosition)
    window.addEventListener('scroll', schedulePosition, true)
    viewport?.addEventListener('resize', schedulePosition)
    viewport?.addEventListener('scroll', schedulePosition)
    const observer = new ResizeObserver(schedulePosition)
    observer.observe(buttonRef.current)
    return () => {
      window.removeEventListener('resize', schedulePosition)
      window.removeEventListener('scroll', schedulePosition, true)
      viewport?.removeEventListener('resize', schedulePosition)
      viewport?.removeEventListener('scroll', schedulePosition)
      observer.disconnect()
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [isOpenPopover, calculatePopoverPosition, compact])

  useEffect(() => {
    if (!compact || !isOpenPopover) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setIsOpenPopover(false)
    }
    // The picker owns Escape before the grid or full-screen view behind it.
    window.addEventListener('keydown', handleEscape, true)
    return () => window.removeEventListener('keydown', handleEscape, true)
  }, [compact, isOpenPopover])

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!isOpenPopover) return
      const target = event.target as Node
      const isClickInsideButton = buttonRef.current?.contains(target)
      const isClickInsidePopover = popoverRef.current?.contains(target)
      if (!isClickInsideButton && !isClickInsidePopover) {
        setIsOpenPopover(false)
      }
    },
    [isOpenPopover]
  )

  useEffect(() => {
    if (isOpenPopover) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpenPopover, handleClickOutside])

  return (
    <>
      <div ref={buttonRef} style={{ position: 'relative' }}>
        <WrapPickerTrigger
          $as={fluid ? 'button' : 'div'}
          type={fluid ? 'button' : undefined}
          aria-label={fluid ? t('Color') : undefined}
          aria-expanded={fluid ? isOpenPopover : undefined}
          $fluid={fluid}
          $style={{
            backgroundImage: isOpenPopover ? `url(${ColorPickerHover})` : `url(${Color})`,
            backgroundSize: isSecondaryStyle || fluid ? 'cover' : 'auto',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
          $isSecondaryStyle={isSecondaryStyle}
          onClick={() => setIsOpenPopover(prev => !prev)}
        >
          <div style={{ background: '#fff' }} />
        </WrapPickerTrigger>
      </div>
      <ColorPickerPopover
        ref={popoverRef}
        isOpen={isOpenPopover}
        position={popoverPosition}
        compactWidth={compact ? popoverPosition?.width : undefined}
        compactMaxHeight={compact ? popoverPosition?.maxHeight : undefined}
        color={colorSelected}
        onColorChange={newColor => onChange(newColor, false)}
        onColorFinalChange={color => onChange(color, true)}
      />
    </>
  )
}

export default ColorPickerButton
